import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import { Table, Button, Popconfirm, Space, Tag } from "antd";

import styles from "../ProjectTeamPage.module.css";
import { IProjectTeamMember } from "../types";

interface MembersTableProps {
  members: IProjectTeamMember[];
  loading: boolean;
  onEdit: (member: IProjectTeamMember) => void;
  onDelete: (member: IProjectTeamMember) => void;
}

// A member only ever gets one status tag/highlight — `IsNewFromSync` (green)
// takes priority over `IsUpdatedFromSync` (yellow).
const renderStatusTag = (record: IProjectTeamMember) => {
  if (record.IsNewFromSync) {
    return (
      <Tag color="green" bordered={false}>
        New
      </Tag>
    );
  }
  if (record.IsUpdatedFromSync) {
    return (
      <Tag color="gold" bordered={false}>
        Updated
      </Tag>
    );
  }
  return null;
};

const getRowClassName = (record: IProjectTeamMember) => {
  if (record.IsNewFromSync) {
    return styles.newRow;
  }
  if (record.IsUpdatedFromSync) {
    return styles.updatedRow;
  }
  return "";
};

const MembersTable = ({
  members,
  loading,
  onEdit,
  onDelete
}: MembersTableProps) => {
  const columns = [
    {
      title: "Full Name",
      dataIndex: "FullName",
      key: "FullName",
      render: (name: string, record: IProjectTeamMember) => (
        <Space>
          <span>{name?.trim() || "—"}</span>
          {renderStatusTag(record)}
        </Space>
      )
    },
    {
      title: "Email",
      dataIndex: "Email",
      key: "Email"
    },
    {
      title: "Role",
      dataIndex: "RoleDisplayName",
      key: "RoleDisplayName",
      // Auto-assigned users arrive with a blank placeholder role (" ") until an
      // admin edits them — flag that so it reads as an action, not empty data.
      render: (role: string) =>
        role?.trim() ? role : <Tag color="orange">Unassigned</Tag>
    },
    {
      title: "Region",
      dataIndex: "Region",
      key: "Region"
    },
    {
      title: "Project",
      dataIndex: "Project",
      key: "Project"
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: IProjectTeamMember) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => onEdit(record)}
          />
          <Popconfirm
            title="Remove team member"
            description="Are you sure you want to remove this team member?"
            onConfirm={() => onDelete(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <Table
      columns={columns}
      dataSource={members}
      loading={loading}
      rowKey={(record) => `${record.Email}-${record.Project}`}
      rowClassName={getRowClassName}
      scroll={{ x: true }}
    />
  );
};

export default MembersTable;
